import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useShowAlert } from 'hooks';
import isNil from 'lodash/isNil';

import { RESUME_SORT_OPTIONS } from '../../ResumeSearch.constants';
import { searchResumes } from '../../ResumeSearch.services';
import { normalizeResumeSearchResponse } from '../../ResumeSearch.utils';

import { FILTER_CONFIG, FILTER_DEFAULT_VALUES, PRIORITY_MAP } from './Filters.constants';
import { createFiltersRequestPayload, stripSavedSearchAccuracy } from './Filters.utils';

const DEFAULT_ORDER = RESUME_SORT_OPTIONS[0].order;

const isFilterValueActive = value => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return !isNil(value);
};

const isFilterValueObject = value =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const createMergedDefaultFilterValues = savedSearchValues => {
  const mergedValues = Object.entries(FILTER_DEFAULT_VALUES).reduce(
    (acc, [filterName, filterValue]) => {
      acc[filterName] = { ...filterValue };
      return acc;
    },
    {}
  );

  if (!isFilterValueObject(savedSearchValues)) {
    return mergedValues;
  }

  Object.entries(savedSearchValues).forEach(([filterName, filterValue]) => {
    if (!Object.prototype.hasOwnProperty.call(mergedValues, filterName)) {
      return;
    }

    if (
      !isFilterValueObject(filterValue) ||
      !Object.prototype.hasOwnProperty.call(filterValue, 'value')
    ) {
      return;
    }

    mergedValues[filterName] = {
      ...mergedValues[filterName],
      ...filterValue,
      value: filterValue.value
    };
  });

  return mergedValues;
};

export const useFiltersForm = ({
  onClearResults,
  onClearSelection,
  onGetResults,
  onSubmit,
  order = DEFAULT_ORDER,
  size
}) => {
  const [uiState, setUiState] = useState({
    submitting: false
  });
  const { register, unregister, setValue, watch, reset, getValues } = useForm({
    defaultValues: FILTER_DEFAULT_VALUES
  });
  const previousAppliedFiltersRef = useRef(FILTER_DEFAULT_VALUES);
  const [appliedFilters, setAppliedFilters] = useState(FILTER_DEFAULT_VALUES);
  const { showAlert } = useShowAlert();

  useEffect(() => {
    FILTER_CONFIG.forEach(config => {
      register({ name: config.name });
    });

    return () => {
      FILTER_CONFIG.forEach(config => {
        unregister(config.name);
      });
    };
  }, [register, unregister]);

  const formValues = watch();

  const hasActiveFilters = useMemo(
    () =>
      Object.values(formValues).some(filter => {
        if (!filter || filter.isDateRange) {
          return false;
        }
        return isFilterValueActive(filter.value);
      }),
    [formValues]
  );

  const handleFilterChange = useCallback(
    (filterName, val) => {
      setValue(filterName, val);
    },
    [setValue]
  );

  const handleAdvancedSearch = useCallback(async () => {
    const formData = getValues();
    try {
      // Clear selection before API call so it batches with other sync state updates
      if (onClearSelection) onClearSelection();
      setUiState(prev => ({ ...prev, submitting: true }));
      const createdPayload = createFiltersRequestPayload(formData, PRIORITY_MAP);
      const searchResponse = await searchResumes({ ...createdPayload, order, size });
      const normalized = normalizeResumeSearchResponse(searchResponse);
      previousAppliedFiltersRef.current = formData;
      setAppliedFilters(formData);
      // Set submitting false before onGetResults so loading states update together
      setUiState(prev => ({ ...prev, submitting: false }));
      onGetResults(normalized);
      if (onSubmit) onSubmit();
    } catch (error) {
      setUiState(prev => ({ ...prev, submitting: false, error }));
      showAlert({
        severity: 'error',
        error
      });
    }
  }, [getValues, onGetResults, onSubmit, onClearSelection, showAlert, order, size]);

  const handleClearSearch = useCallback(() => {
    reset(FILTER_DEFAULT_VALUES);
    previousAppliedFiltersRef.current = FILTER_DEFAULT_VALUES;
    setAppliedFilters(FILTER_DEFAULT_VALUES);
    if (onClearResults) onClearResults();
  }, [reset, onClearResults]);

  const applySavedSearch = useCallback(
    searchValues => {
      const safeSearchValues = isFilterValueObject(searchValues) ? searchValues : {};
      const mergedValues = createMergedDefaultFilterValues(
        stripSavedSearchAccuracy(safeSearchValues)
      );

      reset(mergedValues);
      previousAppliedFiltersRef.current = mergedValues;
      setAppliedFilters(mergedValues);
    },
    [reset]
  );

  const getSerializableSearch = useCallback(() => {
    const currentValues = getValues();

    if (!currentValues || typeof currentValues !== 'object') {
      return {};
    }

    return Object.entries(currentValues).reduce((acc, [filterName, filterValue]) => {
      if (!filterValue || !isFilterValueActive(filterValue.value)) {
        return acc;
      }

      acc[filterName] = filterValue;
      return acc;
    }, {});
  }, [getValues]);

  const getFiltersFormValues = useCallback(() => previousAppliedFiltersRef.current, []);

  return useMemo(
    () => ({
      uiState,
      formValues,
      appliedFilters,
      hasActiveFilters,
      getFiltersFormValues,
      applySavedSearch,
      getSerializableSearch,
      handleFilterChange,
      handleAdvancedSearch,
      handleClearSearch
    }),
    [
      uiState,
      formValues,
      appliedFilters,
      hasActiveFilters,
      getFiltersFormValues,
      applySavedSearch,
      getSerializableSearch,
      handleFilterChange,
      handleAdvancedSearch,
      handleClearSearch
    ]
  );
};
